import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, Copy, Check } from "lucide-react";

// Simple brand icons as inline SVG so we don't depend on brand-specific libraries
const WhatsAppIcon = ({ size = 22, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden="true">
    <path fill="currentColor" d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.09.55 4.13 1.6 5.93L2 22l4.29-1.12a9.9 9.9 0 0 0 5.75 1.85h.01c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm5.66 14.19c-.24.68-1.41 1.3-1.94 1.35-.5.05-1.14.07-1.83-.11-.42-.11-.97-.29-1.66-.6-2.92-1.26-4.82-4.2-4.97-4.4-.14-.2-1.18-1.57-1.18-3s.75-2.14 1.02-2.43c.27-.29.59-.36.79-.36.2 0 .4.01.58.02.18.01.44-.07.68.52.24.6.83 2.06.9 2.2.07.14.12.31.02.5-.1.19-.15.31-.29.48-.14.17-.3.38-.43.51-.14.14-.29.29-.13.58.17.29.75 1.24 1.6 2 1.11.99 2.05 1.29 2.34 1.44.29.14.46.12.63-.07.17-.2.73-.85.93-1.14.2-.29.4-.24.68-.14.29.1 1.8.85 2.11 1 .31.15.51.22.59.34.07.12.07.71-.16 1.39Z"/>
  </svg>
);

const FacebookIcon = ({ size = 22, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden="true">
    <path fill="currentColor" d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.91h2.54V9.83c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.77l-.44 2.91h-2.33V22c4.78-.79 8.44-4.94 8.44-9.94Z"/>
  </svg>
);

const ShareModal = ({ open, onOpenChange }) => {
  const posterRef = useRef(null);
  const previewImgRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [posterDataUrl, setPosterDataUrl] = useState("");
  const [building, setBuilding] = useState(false);

  const shareUrl = typeof window !== "undefined" ? window.location.origin + window.location.pathname : "";
  const shareText = "Discover the Sharada Rama Rao Trust — A legacy of selfless service. Join hands, spread kindness.";

  const openWhatsApp = () => {
    const text = encodeURIComponent(`${shareText} ${shareUrl}`);
    // Use device-appropriate URL to avoid api.whatsapp.com (blocked on some networks)
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
    const url = isMobile
      ? `whatsapp://send?text=${text}`
      : `https://web.whatsapp.com/send?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const openFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* ignore */ }
  };

  // Build the poster canvas whenever the modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const build = async () => {
      // Wait for the canvas to be mounted (portal renders on next tick)
      let canvas = posterRef.current;
      let tries = 0;
      while (!canvas && tries < 30) {
        await new Promise((r) => setTimeout(r, 33));
        canvas = posterRef.current;
        tries += 1;
      }
      if (!canvas) {
        console.warn("Poster canvas not available");
        return;
      }
      setBuilding(true);
      try {
        const W = 1080, H = 1350; // portrait Instagram-friendly poster
        canvas.width = W;
        canvas.height = H;
        const ctx = canvas.getContext("2d");

        // Background — warm off-white matching the hero
        ctx.fillStyle = "#f2ecd8";
        ctx.fillRect(0, 0, W, H);

        // Soft terracotta arc (top-right decorative)
        ctx.fillStyle = "rgba(198,120,80,0.14)";
        ctx.beginPath();
        ctx.arc(W - 40, -60, 380, 0, Math.PI * 2);
        ctx.fill();

        // Olive arc (bottom-left)
        ctx.fillStyle = "rgba(107,142,35,0.12)";
        ctx.beginPath();
        ctx.arc(-80, H + 40, 420, 0, Math.PI * 2);
        ctx.fill();

        // Load & draw the SR logo (centered top area)
        const logo = new Image();
        logo.crossOrigin = "anonymous";
        await new Promise((resolve, reject) => {
          logo.onload = resolve;
          logo.onerror = reject;
          logo.src = "/sr-logo.png";
        });
        const logoSize = 420;
        const logoX = (W - logoSize) / 2;
        const logoY = 90;
        ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);

        // Divider line
        ctx.strokeStyle = "#c47750";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(W / 2 - 120, logoY + logoSize + 40);
        ctx.lineTo(W / 2 + 120, logoY + logoSize + 40);
        ctx.stroke();

        // Trust name (big)
        ctx.fillStyle = "#6b8e23";
        ctx.font = "700 68px Quicksand, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Sharada Rama Rao Trust", W / 2, logoY + logoSize + 110);

        // Tagline (italic warm)
        ctx.fillStyle = "#c47750";
        ctx.font = "italic 500 40px 'Caveat', Quicksand, cursive";
        ctx.fillText("A legacy of selfless service", W / 2, logoY + logoSize + 170);

        // Body copy
        ctx.fillStyle = "#2a2a2a";
        ctx.font = "500 32px Quicksand, sans-serif";
        ctx.fillText("Join Hands  ·  Spread Kindness", W / 2, logoY + logoSize + 240);

        // Registration & 80G note
        ctx.fillStyle = "#5a4a3a";
        ctx.font = "400 22px Quicksand, sans-serif";
        ctx.fillText("Registration No. 243/15-16", W / 2, logoY + logoSize + 285);
        ctx.fillText("Donations exempted under Sec 80G(5)(6), Income Tax Act 1961", W / 2, logoY + logoSize + 320);

        // QR code (bottom-left)
        const qrDataUrl = await QRCode.toDataURL(shareUrl, { width: 300, margin: 1, color: { dark: "#3f5520", light: "#ffffff00" } });
        const qrImg = new Image();
        await new Promise((resolve, reject) => {
          qrImg.onload = resolve;
          qrImg.onerror = reject;
          qrImg.src = qrDataUrl;
        });
        const qrSize = 220;
        const qrX = 90;
        const qrY = H - qrSize - 110;
        // white card behind QR
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#e5d7bd";
        ctx.lineWidth = 3;
        const pad = 14;
        ctx.beginPath();
        // simple rounded rect
        const r = 20;
        const bx = qrX - pad, by = qrY - pad, bw = qrSize + pad * 2, bh = qrSize + pad * 2;
        ctx.moveTo(bx + r, by);
        ctx.arcTo(bx + bw, by, bx + bw, by + bh, r);
        ctx.arcTo(bx + bw, by + bh, bx, by + bh, r);
        ctx.arcTo(bx, by + bh, bx, by, r);
        ctx.arcTo(bx, by, bx + bw, by, r);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

        // "Scan to visit" text next to QR
        ctx.fillStyle = "#3a2c25";
        ctx.font = "700 30px Quicksand, sans-serif";
        ctx.textAlign = "left";
        ctx.fillText("Scan to visit", qrX + qrSize + 60, qrY + qrSize / 2 - 15);
        ctx.fillStyle = "#5a4a3a";
        ctx.font = "400 22px Quicksand, sans-serif";
        ctx.fillText("our website & learn more", qrX + qrSize + 60, qrY + qrSize / 2 + 20);
        // URL below
        ctx.font = "500 18px 'Courier New', monospace";
        ctx.fillStyle = "#3f5520";
        const displayUrl = shareUrl.replace(/^https?:\/\//, "");
        ctx.fillText(displayUrl, qrX + qrSize + 60, qrY + qrSize / 2 + 55);

        if (!cancelled) {
          setPosterDataUrl(canvas.toDataURL("image/png"));
        }
      } catch (e) {
        console.error("Poster build failed", e);
      } finally {
        if (!cancelled) setBuilding(false);
      }
    };
    build();
    return () => { cancelled = true; };
  }, [open, shareUrl]);

  const downloadPoster = () => {
    if (!posterDataUrl) return;
    const a = document.createElement("a");
    a.href = posterDataUrl;
    a.download = "sharada-rama-rao-trust-share-poster.png";
    a.click();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl bg-[#faf3e2] border-[#e5d7bd]">
        <DialogHeader>
          <DialogTitle className="text-[#3a2c25] text-xl">Spread the word</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: Share buttons */}
          <div className="flex flex-col gap-3">
            <p className="text-sm text-[#5a4a3a]">
              Share the Trust with your friends and family in a click.
            </p>

            <button
              onClick={openWhatsApp}
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#25d366] hover:bg-[#1eb85a] text-white font-semibold shadow-md transition-colors"
            >
              <WhatsAppIcon size={22} />
              <span>Share on WhatsApp</span>
            </button>

            <button
              onClick={openFacebook}
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#1877f2] hover:bg-[#155fc5] text-white font-semibold shadow-md transition-colors"
            >
              <FacebookIcon size={22} />
              <span>Share on Facebook</span>
            </button>

            <button
              onClick={copyLink}
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white hover:bg-[#f4ecda] text-[#3a2c25] font-semibold shadow-md border border-[#e5d7bd] transition-colors"
            >
              {copied ? <Check size={22} className="text-[#5c7c2e]" /> : <Copy size={22} />}
              <span>{copied ? "Link copied!" : "Copy link"}</span>
            </button>

            <div className="mt-4 pt-4 border-t border-[#e5d7bd]">
              <p className="text-sm font-semibold text-[#3a2c25] mb-2">Downloadable poster</p>
              <p className="text-xs text-[#5a4a3a] mb-3">
                Grab this share-ready poster with our logo & QR code. Post it on Instagram Stories, WhatsApp status or print it.
              </p>
              <Button
                onClick={downloadPoster}
                disabled={!posterDataUrl || building}
                className="w-full bg-[#c47750] hover:bg-[#b46540] text-white rounded-full font-semibold disabled:opacity-60"
              >
                <Download size={16} className="mr-1.5" />
                {building ? "Generating poster…" : "Download poster"}
              </Button>
            </div>
          </div>

          {/* Right: Poster preview */}
          <div className="flex items-center justify-center bg-[#e6dcc2] rounded-xl p-3 min-h-[320px]">
            {posterDataUrl ? (
              <img
                ref={previewImgRef}
                src={posterDataUrl}
                alt="Shareable poster preview"
                className="w-full h-auto max-h-[360px] object-contain rounded shadow-md"
              />
            ) : (
              <div className="text-[#7a6553] text-sm">Generating preview…</div>
            )}
          </div>
        </div>

        {/* Off-screen canvas used to generate the poster (kept mounted always) */}
      </DialogContent>
      </Dialog>
      <canvas ref={posterRef} style={{ position: "fixed", left: "-99999px", top: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }} aria-hidden="true" />
    </>
  );
};

export default ShareModal;
